import { useLenis } from 'lenis/react'
import { AnimatePresence, m, useReducedMotion } from 'motion/react'
import { useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { CallBar } from '@/components/call-bar'
import { ClarityAnalytics } from '@/components/clarity-analytics'
import { CookieConsent } from '@/components/cookie-consent'
import { CursorDot } from '@/components/cursor-dot'
import { FloatingContact } from '@/components/floating-contact'
import { GrainOverlay } from '@/components/grain-overlay'
import { IntroLoader } from '@/components/intro-loader'
import { SiteFooter } from '@/components/site-footer'
import { SiteHeader } from '@/components/site-header'
import { SkipLink } from '@/components/skip-link'

export function Layout() {
  const location = useLocation()
  const reduceMotion = useReducedMotion()
  const mainRef = useRef<HTMLElement>(null)
  const firstRender = useRef(true)
  const lenis = useLenis()

  // React Router doesn't reset scroll or focus on navigation the way a full
  // page load does -- clicking a nav link while scrolled down a long page
  // (e.g. the homepage) left the next page wherever the viewport already
  // was, and a screen reader user got silence, their virtual cursor still on
  // the previous page. Skipped on the very first render, which would
  // otherwise steal the scroll position/focus a fresh page load naturally
  // starts with.
  //
  // Scroll specifically has to go through Lenis's own scrollTo, not
  // window.scrollTo or the scroll-into-view a plain .focus() would trigger --
  // Lenis (src/components/smooth-scroll.tsx) virtualizes the page's scroll
  // entirely, so the browser's native scroll behavior no longer reflects
  // what's actually on screen. useLenis() returns undefined when Lenis isn't
  // mounted (prefers-reduced-motion visitors, who get plain native scroll
  // instead), so window.scrollTo covers that case.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    if (lenis) {
      lenis.scrollTo(0, { immediate: true })
    } else {
      window.scrollTo(0, 0)
    }
    mainRef.current?.focus({ preventScroll: true })
  }, [location.pathname, lenis])

  return (
    <div className="relative flex min-h-screen flex-col overflow-x-hidden bg-canvas pb-24 text-ink min-[900px]:pb-0">
      <ClarityAnalytics />
      <IntroLoader />
      <GrainOverlay />
      <CursorDot />
      <SkipLink />
      <SiteHeader />
      <main id="main" ref={mainRef} tabIndex={-1} className="flex-1 outline-none">
        {reduceMotion ? (
          <div id="main-top">
            <Outlet />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <m.div
              key={location.pathname}
              id="main-top"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            >
              <Outlet />
            </m.div>
          </AnimatePresence>
        )}
      </main>
      <SiteFooter />
      <CallBar />
      <FloatingContact />
      <CookieConsent />
    </div>
  )
}
